import { getUserId } from "./fingerprint";
import { withBasePath } from "./base-path";

export class ApiError extends Error {
  /**
   * 🔴 2026-09-29：把服务端响应体挂在错误上 —— 422「视频提示词不完整」这类
   * 结构化错误体里带逐镜 results（每镜失败原因），调用方要拿它回填到卡片 UI。
   * 旧实现只留 message 字符串，逐镜原因全丢。
   */
  constructor(public status: number, message: string, public body?: unknown) {
    super(message);
    this.name = "ApiError";
  }
}

/** Read userId from cookie as a fallback (Cookie is set by middleware). */
function getUserIdFromCookie(): string | null {
  const match = document.cookie.match(/(?:^|;\s*)ai_comic_uid=([^;]+)/);
  return match ? match[1] : null;
}

/** 把自愈后的身份写回 App 读取身份的两处（localStorage + cookie）。 */
function persistUserId(userId: string) {
  try {
    localStorage.setItem("ai_comic_uid", userId);
  } catch {}
  document.cookie = `ai_comic_uid=${userId}; path=/; max-age=31536000; samesite=lax`;
}

/**
 * 身份自愈：向服务端问一次「真正拥有项目的 uid」。
 * 旧逻辑只在首页纠正过期身份，深链直接进项目页时接口全 404、页面永远卡在「加载中」。
 */
async function healIdentity(): Promise<string | null> {
  try {
    const res = await fetch(withBasePath("/api/identity"), { cache: "no-store" });
    if (!res.ok) return null;
    const data = (await res.json()) as { userId?: string };
    if (!data.userId) return null;
    persistUserId(data.userId);
    return data.userId;
  } catch {
    return null;
  }
}

/**
 * 判断响应是否是「集合类接口返回空数组」。
 *
 * 只在 response.clone() 上读，且读完即弃：body 一旦被消费，原 response 就废了。
 * 非 JSON / 非数组 / 非空 一律返回 false —— 宁可漏自愈，不可误伤。
 * 空对象 {} 不算（有些接口合法返回空对象），只有 [] 算集合。
 */
async function isEmptyCollection(response: Response): Promise<boolean> {
  if (response.status !== 200) return false;
  try {
    const type = response.headers.get("content-type") || "";
    if (!type.includes("application/json")) return false;
    const body = (await response.clone().json()) as unknown;
    return Array.isArray(body) && body.length === 0;
  } catch {
    return false;
  }
}

export async function apiFetch(url: string, options: RequestInit = {}): Promise<Response> {
  // Priority: localStorage → cookie (middleware fallback)
  const userId = getUserId() || getUserIdFromCookie();
  const headers = new Headers(options.headers);
  if (userId) headers.set("x-user-id", userId);
  let response: Response;
  try {
    response = await fetch(withBasePath(url), { ...options, headers });
  } catch (err) {
    // 诊断加固（2026-09-16）：网络层失败最常见的真因是 dev 服务没起来/正在重启/端口漂移，
    // 原来只会弹 "Failed to fetch"，看不出去看哪里。
    // 2026-10-04：区分调用方主动超时中断（AbortError），避免误报成「服务不可达」。
    if (err instanceof Error && err.name === "AbortError") {
      throw new ApiError(0, url.split("?")[0] + " · 请求超时（已主动中断）");
    }
    throw new ApiError(0, url.split("?")[0] + " · 服务不可达（门户 dev 未运行或正在重启）");
  }

  // 身份自愈：404，以及「集合类接口返回 200 + 空数组」。
  //
  // 🔴 2026-10-06：为什么必须加空数组这一支。
  // middleware（src/proxy.ts）会给**任何新 origin** 的浏览器发一个随机 uid：
  // cookie/localStorage 都按 origin 隔离，所以从公网 Funnel、局域网 IP 首次访问时，
  // 浏览器带着一个随机 uid → `/api/projects` 的 `where(userId=随机值)` 查不到行，
  // 但它**返回 200 + []**（不是 404），旧自愈分支根本不触发 → 页面永远空白。
  // 深链接口才 404，所以旧逻辑只在「深链 404」时才自愈，列表页漏了。
  if (response.status === 404 || (await isEmptyCollection(response))) {
    const healed = await healIdentity();
    if (healed && healed !== userId) {
      const retryHeaders = new Headers(options.headers);
      retryHeaders.set("x-user-id", healed);
      response = await fetch(withBasePath(url), { ...options, headers: retryHeaders });
    }
  }

  if (!response.ok) {
    let detail = "";
    let parsedBody: unknown;
    try {
      parsedBody = await response.clone().json();
      const body = parsedBody as { error?: string; detail?: string };
      detail = body.error || body.detail || "";
    } catch {}
    // 诊断加固（2026-09-16）：错误文案必须自带「哪个接口 · 什么状态码 · 服务端原因」。
    // 当天 dev 双实例写坏 Turbopack 缓存 → 接口 404/挂 45s，界面只弹「生成失败」，
    // 提示与真因完全脱节，只能靠翻 dev 日志才定位。
    const endpoint = url.split("?")[0];
    throw new ApiError(response.status, endpoint + " · HTTP " + response.status + (detail ? " · " + detail : ""), parsedBody);
  }
  return response;
}
